import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

import api from '@src/api/clients';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';

const STATUS_OPTIONS = ['Approved', 'Pending Approval', 'Draft', 'Disproved'];
const STATUS_COLORS = {
  Approved: 'success.main',
  'Pending Approval': 'warning.main',
  Draft: 'info.main',
  Disproved: 'error.main',
};

const formatCurrency = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return '—';
  }
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i,
  label: new Date(0, i).toLocaleString('default', { month: 'long' }),
}));

const BULK_MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(0, i).toLocaleString('default', { month: 'long' }),
}));

const EMPTY_BUDGET = {
  client: '',
  date: '',
  vertical: '',
  location: '',
  channel: '',
  amount: '',
  status: '',
  notes: '',
};

export default function ClientBudgetsPage() {
  const [budgets, setBudgets] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [channels, setChannels] = useState([]);
  const [locations, setLocations] = useState([]);

  const [statusFilter, setStatusFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState(new Date().getUTCMonth());
  const [clientFilter, setClientFilter] = useState('');
  const [verticalFilter, setVerticalFilter] = useState('');
  const [parentCompanyFilter, setParentCompanyFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatusValue, setBulkStatusValue] = useState('');
  const [open, setOpen] = useState(false);
  const [currentBudget, setCurrentBudget] = useState(EMPTY_BUDGET);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error',
  });
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const today = new Date();
  const nextMonthDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 1));
  const [bulkSourceMonth, setBulkSourceMonth] = useState(today.getUTCMonth() + 1);
  const [bulkSourceYear, setBulkSourceYear] = useState(today.getUTCFullYear());
  const [bulkTargetMonth, setBulkTargetMonth] = useState(nextMonthDate.getUTCMonth() + 1);
  const [bulkTargetYear, setBulkTargetYear] = useState(nextMonthDate.getUTCFullYear());
  const [bulkSelectedClients, setBulkSelectedClients] = useState([]);
  const [bulkSelectedParentCompanies, setBulkSelectedParentCompanies] = useState([]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkError, setBulkError] = useState('');

  const {
    options: clientOptions,
    loading: clientsLoading,
    handleInputChange: handleClientSearch,
    addOption: addClientOption,
  } = useClientAutocomplete();

  const getErrorMessage = (err, fallback) => {
    const data = err.response?.data;
    if (!data) return fallback;
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    if (typeof data === 'object') {
      return Object.entries(data)
        .map(
          ([field, msg]) =>
            `${field}: ${Array.isArray(msg) ? msg.join(', ') : msg}`
        )
        .join(', ');
    }
    return fallback;
  };

  const fetchBudgets = () => {
    api
      .request({ url: 'dashboard/client-budgets/', method: 'GET' })
      .then((res) => {
        const normalizedBudgets = res.data.map((b) => {
          const budget = { ...b, date: b.date ?? b.budget_date };

          const clientData = b.client_data;
          if (clientData?.id && clientData?.name) {
            addClientOption({ value: clientData.id, label: clientData.name });
          }

          return budget;
        });

        setBudgets(normalizedBudgets);
      })
      .catch((err) =>
        setSnackbar({
          open: true,
          message: getErrorMessage(err, 'Failed to load budgets'),
          severity: 'error',
        })
      );
  };

  useEffect(() => {
    fetchBudgets();
  }, []);

  useEffect(() => {
    api.request({ url: 'dashboard/verticals/', method: 'GET' }).then((res) =>
      setVerticals(res.data.map((v) => ({ value: v.id, label: v.vertical })))
    );
    api.request({ url: 'dashboard/channels/', method: 'GET' }).then((res) =>
      setChannels(res.data.map((c) => ({ value: c.id, label: c.name })))
    );
  }, []);

  useEffect(() => {
    if (currentBudget.client) {
      api
        .request({
          url: `dashboard/client-territories/?client_id=${currentBudget.client}`,
          method: 'GET',
        })
        .then((res) =>
          setLocations(res.data.map((l) => ({ value: l.id, label: l.name })))
        );
    } else {
      setLocations([]);
    }
  }, [currentBudget.client]);

  const handleOpen = (budget = EMPTY_BUDGET) => {
    setCurrentBudget({
      ...EMPTY_BUDGET,
      ...budget,
      vertical: budget.vertical ?? budget.vertical_data?.id ?? '',
      location: budget.location ?? budget.location_data?.id ?? '',
      channel: budget.channel ?? budget.channel_data?.id ?? '',
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentBudget(EMPTY_BUDGET);
  };

  const handleChange = (field, value) => {
    setCurrentBudget((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const method = currentBudget.id ? 'PUT' : 'POST';
    const url = `dashboard/client-budgets/${currentBudget.id ? `${currentBudget.id}/` : ''}`;
    const payload = {
      ...currentBudget,
      vertical: currentBudget.vertical || null,
      location: currentBudget.location || null,
      channel: currentBudget.channel || null,
    };
    api
      .request({ url, method, payload })
      .then(() => {
        handleClose();
        fetchBudgets();
      })
      .catch((err) =>
        setSnackbar({
          open: true,
          message: getErrorMessage(err, 'Failed to save budget'),
          severity: 'error',
        })
      );
  };

  const handleDelete = (id) => {
    if (!window.confirm('Delete this budget?')) return;
    api
      .request({ url: `dashboard/client-budgets/${id}/`, method: 'DELETE' })
      .then(fetchBudgets)
      .catch((err) =>
        setSnackbar({
          open: true,
          message: getErrorMessage(err, 'Failed to delete budget'),
          severity: 'error',
        })
      );
  };

  const handleOpenBulkDialog = () => {
    setBulkDialogOpen(true);
    setBulkError('');
    setBulkSourceMonth(today.getUTCMonth() + 1);
    setBulkSourceYear(today.getUTCFullYear());
    setBulkTargetMonth(nextMonthDate.getUTCMonth() + 1);
    setBulkTargetYear(nextMonthDate.getUTCFullYear());
    setBulkSelectedClients([]);
    setBulkSelectedParentCompanies([]);
  };

  const handleCloseBulkDialog = () => {
    setBulkDialogOpen(false);
    setBulkError('');
  };

  const handleBulkSubmit = () => {
    if (!bulkSourceMonth || !bulkSourceYear || !bulkTargetMonth || !bulkTargetYear) {
      setBulkError('Select both source and target month/year.');
      return;
    }
    if (bulkSelectedClients.length === 0 && bulkSelectedParentCompanies.length === 0) {
      setBulkError('Select at least one client or parent company.');
      return;
    }

    setBulkError('');
    setBulkSubmitting(true);
    api
      .request({
        url: 'dashboard/client-budgets/bulk-duplicate/',
        method: 'POST',
        payload: {
          source_month: bulkSourceMonth,
          source_year: bulkSourceYear,
          target_month: bulkTargetMonth,
          target_year: bulkTargetYear,
          client_ids: bulkSelectedClients.map((client) => client.value),
          parent_company_ids: bulkSelectedParentCompanies.map((pc) => pc.value),
        },
      })
      .then((res) => {
        const createdCount = res.data?.created?.length || 0;
        const skippedCount = res.data?.skipped_client_ids?.length || 0;
        setSnackbar({
          open: true,
          message: `${createdCount} budget(s) duplicated.${skippedCount ? ` ${skippedCount} skipped because they already exist.` : ''}`,
          severity: skippedCount ? 'warning' : 'success',
        });
        fetchBudgets();
        setBulkDialogOpen(false);
        setBulkSourceMonth(today.getUTCMonth() + 1);
        setBulkSourceYear(today.getUTCFullYear());
        setBulkTargetMonth(nextMonthDate.getUTCMonth() + 1);
        setBulkTargetYear(nextMonthDate.getUTCFullYear());
        setBulkSelectedClients([]);
        setBulkSelectedParentCompanies([]);
      })
      .catch((err) => {
        setBulkError(getErrorMessage(err, 'Failed to duplicate budgets'));
      })
      .finally(() => setBulkSubmitting(false));
  };

  function getClientName(budget) {
    if (!budget) {
      return '';
    }

    if (budget.client_data?.name) {
      return budget.client_data.name;
    }

    const option = clientOptions.find((c) => c.value === budget.client);
    if (option?.label) {
      return option.label;
    }

    return budget.client ? String(budget.client) : '';
  }

  const parentCompanyOptions = useMemo(() => {
    const unique = new Map();
    budgets.forEach((b) => {
      const id = b.client_data?.parent_company;
      const name = b.client_data?.parent_company_name;
      if (id !== null && id !== undefined && name) {
        unique.set(id, name);
      }
    });

    return Array.from(unique.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [budgets]);

  useEffect(() => {
    setSelectedIds((prev) =>
      prev.filter((id) => budgets.some((budget) => budget.id === id))
    );
  }, [budgets]);

  const handleToggleAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredBudgets.map((b) => b.id));
      return;
    }
    setSelectedIds([]);
  };

  const handleToggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkStatusUpdate = () => {
    if (!bulkStatusValue) {
      setSnackbar({
        open: true,
        message: 'Select a status to apply.',
        severity: 'error',
      });
      return;
    }
    if (selectedIds.length === 0) {
      setSnackbar({
        open: true,
        message: 'Select at least one budget to update.',
        severity: 'error',
      });
      return;
    }
    api
      .request({
        url: 'dashboard/client-budgets/bulk-status/',
        method: 'POST',
        payload: { ids: selectedIds, status: bulkStatusValue },
      })
      .then(() => {
        setSnackbar({
          open: true,
          message: 'Statuses updated.',
          severity: 'success',
        });
        setSelectedIds([]);
        setBulkStatusValue('');
        fetchBudgets();
      })
      .catch((err) =>
        setSnackbar({
          open: true,
          message: getErrorMessage(err, 'Failed to update statuses'),
          severity: 'error',
        })
      );
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      setSnackbar({
        open: true,
        message: 'Select at least one budget to delete.',
        severity: 'error',
      });
      return;
    }
    if (!window.confirm(`Delete ${selectedIds.length} selected budget(s)?`)) {
      return;
    }
    api
      .request({
        url: 'dashboard/client-budgets/bulk-delete/',
        method: 'POST',
        payload: { ids: selectedIds },
      })
      .then(() => {
        setSnackbar({
          open: true,
          message: 'Selected budgets deleted.',
          severity: 'success',
        });
        setSelectedIds([]);
        fetchBudgets();
      })
      .catch((err) =>
        setSnackbar({
          open: true,
          message: getErrorMessage(err, 'Failed to delete budgets'),
          severity: 'error',
        })
      );
  };

  const filteredBudgets = budgets.filter(
    (b) =>
      (!statusFilter || b.status === statusFilter) &&
      (monthFilter === -1 || new Date(b.date).getUTCMonth() === monthFilter) &&
      (!clientFilter || b.client === clientFilter) &&
      (!parentCompanyFilter ||
        b.client_data?.parent_company === parentCompanyFilter) &&
      (!
        verticalFilter ||
        b.vertical === verticalFilter ||
        b.vertical_data?.id === verticalFilter
      )
  );

  if (clientOptions.length > 0) {
    filteredBudgets.sort((a, b) =>
      getClientName(a).localeCompare(getClientName(b))
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Client Budgets</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={handleOpenBulkDialog}>Bulk duplicate</Button>
          <Button variant="contained" onClick={() => handleOpen()}>+ Add Budget</Button>
        </Stack>
      </Box>

      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2} mb={2} alignItems="center" justifyContent="space-between">
            <Box display="flex" gap={2} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Set status</InputLabel>
                <Select
                  label="Set status"
                  value={bulkStatusValue}
                  onChange={(e) => setBulkStatusValue(e.target.value)}
                >
                  <MenuItem value="">Select status</MenuItem>
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" disabled={!selectedIds.length} onClick={handleBulkStatusUpdate}>
                Apply status
              </Button>
              <Button variant="outlined" color="error" disabled={!selectedIds.length} onClick={handleBulkDelete}>
                Delete selected
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {selectedIds.length} selected
            </Typography>
          </Stack>

          <Box mb={2} display="flex" gap={2} flexWrap="wrap">
            <Autocomplete
              options={clientOptions}
              getOptionLabel={(option) => option.label}
              value={
                clientOptions.find((c) => c.value === clientFilter) || null
              }
              onChange={(e, val) => setClientFilter(val ? val.value : '')}
              onInputChange={handleClientSearch}
              loading={clientsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client"
                  size="small"
                  sx={{ minWidth: 200 }}
                />
              )}
            />
            <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Vertical</InputLabel>
              <Select
                label="Vertical"
                value={verticalFilter}
                onChange={(e) => setVerticalFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {verticals.map((v) => (
                  <MenuItem key={v.value} value={v.value}>
                    {v.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Parent Company</InputLabel>
              <Select
                label="Parent Company"
                value={parentCompanyFilter}
                onChange={(e) => {
                  const value =
                    e.target.value === '' ? '' : Number(e.target.value);
                  setParentCompanyFilter(value);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {parentCompanyOptions.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Month</InputLabel>
              <Select
                label="Month"
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
              >
                <MenuItem value={-1}>All</MenuItem>
                {MONTH_OPTIONS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedIds.length > 0 &&
                        selectedIds.length < filteredBudgets.length
                      }
                      checked={
                        filteredBudgets.length > 0 &&
                        selectedIds.length === filteredBudgets.length
                      }
                      onChange={(e) => handleToggleAll(e.target.checked)}
                      inputProps={{ 'aria-label': 'Select all budgets' }}
                    />
                  </TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Vertical</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>MTD Spend</TableCell>
                  <TableCell>Projected Spend</TableCell>
                  <TableCell>Remaining / Day</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBudgets.map((b) => (
                  <TableRow key={b.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(b.id)}
                        onChange={() => handleToggleOne(b.id)}
                        inputProps={{ 'aria-label': `Select budget ${b.id}` }}
                      />
                    </TableCell>
                    <TableCell>{getClientName(b)}</TableCell>
                    <TableCell>{b.date}</TableCell>
                    <TableCell>{b.vertical_data?.vertical || '-'}</TableCell>
                    <TableCell>{b.location_data?.name || '-'}</TableCell>
                    <TableCell>{b.channel_data?.name || '-'}</TableCell>
                    <TableCell>{formatCurrency(b.amount)}</TableCell>
                    <TableCell>{formatCurrency(b.month_to_date_spend)}</TableCell>
                    <TableCell>{formatCurrency(b.projected_spend)}</TableCell>
                    <TableCell>{formatCurrency(b.remaining_per_day)}</TableCell>
                    <TableCell sx={{ color: STATUS_COLORS[b.status] }}>{b.status || '-'}</TableCell>
                    <TableCell>{b.notes || '-'}</TableCell>
                    <TableCell>{b.last_updated ? new Date(b.last_updated).toLocaleString() : '-'}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => handleOpen(b)}>Edit</Button>
                        <Button size="small" onClick={() => handleOpen({ ...b, id: undefined })}>Duplicate</Button>
                        <Button size="small" color="error" onClick={() => handleDelete(b.id)}>Delete</Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={bulkDialogOpen} onClose={handleCloseBulkDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk duplicate budgets</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Autocomplete
              multiple
              options={clientOptions}
              getOptionLabel={(option) => option.label}
              value={bulkSelectedClients}
              onChange={(e, val) => setBulkSelectedClients(val || [])}
              onInputChange={handleClientSearch}
              loading={clientsLoading}
              filterSelectedOptions
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Clients"
                  placeholder="Select one or more clients"
                />
              )}
            />
            <Autocomplete
              multiple
              options={parentCompanyOptions}
              getOptionLabel={(option) => option.label}
              value={bulkSelectedParentCompanies}
              onChange={(e, val) => setBulkSelectedParentCompanies(val || [])}
              filterSelectedOptions
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parent companies"
                  placeholder="Select one or more parent companies"
                />
              )}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Source month</InputLabel>
                <Select
                  label="Source month"
                  value={bulkSourceMonth}
                  onChange={(e) => setBulkSourceMonth(Number(e.target.value))}
                >
                  {BULK_MONTH_OPTIONS.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                type="number"
                label="Source year"
                value={bulkSourceYear}
                onChange={(e) => setBulkSourceYear(Number(e.target.value))}
                InputProps={{ inputProps: { min: 1900, max: 3000 } }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Target month</InputLabel>
                <Select
                  label="Target month"
                  value={bulkTargetMonth}
                  onChange={(e) => setBulkTargetMonth(Number(e.target.value))}
                >
                  {BULK_MONTH_OPTIONS.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                type="number"
                label="Target year"
                value={bulkTargetYear}
                onChange={(e) => setBulkTargetYear(Number(e.target.value))}
                InputProps={{ inputProps: { min: 1900, max: 3000 } }}
              />
            </Stack>
            <Alert severity="info">
              Duplicates all budgets for the selected clients in the source month into the target month. Status is set to Pending Approval on the copies.
            </Alert>
            {bulkError && <Alert severity="error">{bulkError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkSubmit} disabled={bulkSubmitting}>
            {bulkSubmitting ? 'Duplicating...' : 'Duplicate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{currentBudget.id ? 'Edit Budget' : 'Add Budget'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Autocomplete
              options={clientOptions}
              getOptionLabel={(option) => option.label}
              value={
                clientOptions.find((c) => c.value === currentBudget.client) ||
                null
              }
              onChange={(e, val) =>
                handleChange('client', val ? val.value : '')
              }
              onInputChange={handleClientSearch}
              loading={clientsLoading}
              renderInput={(params) => <TextField {...params} label="Client" />}
            />
            <TextField
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={currentBudget.date}
              onChange={(e) => handleChange('date', e.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel>Vertical</InputLabel>
              <Select
                label="Vertical"
                value={currentBudget.vertical || ''}
                onChange={(e) => handleChange('vertical', e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {verticals.map((v) => (
                  <MenuItem key={v.value} value={v.value}>
                    {v.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Location</InputLabel>
              <Select
                label="Location"
                value={currentBudget.location || ''}
                onChange={(e) => handleChange('location', e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {locations.map((l) => (
                  <MenuItem key={l.value} value={l.value}>
                    {l.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Channel</InputLabel>
              <Select
                label="Channel"
                value={currentBudget.channel || ''}
                onChange={(e) => handleChange('channel', e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {channels.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="number"
              label="Amount"
              value={currentBudget.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={currentBudget.status}
                onChange={(e) => handleChange('status', e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Notes"
              value={currentBudget.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity || 'error'}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
